import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowLeft, BarChart3 } from "lucide-react"
import { adminGetStats, type AdminStats } from "@/lib/supabase"

// Mettre à jour si le prix du plan change.
const MONTHLY_PRICE_EUR = 29.99

type StatCardProps = {
  label: string
  value: string | number
  sub?: string
  color?: string
  delay?: number
}

function StatCard({ label, value, sub, color = "#06b6d4", delay = 0 }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-2xl p-4"
      style={{ background: "rgba(12,20,35,0.6)", border: "1px solid rgba(30,41,59,0.55)" }}
    >
      <p className="text-[11px] text-gray-500 mb-1 uppercase tracking-wide">{label}</p>
      <p className="text-[26px] font-bold leading-tight" style={{ color }}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </motion.div>
  )
}

export function AdminStatsPage() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    adminGetStats()
      .then((data) => { if (mounted) setStats(data) })
      .catch((err: Error) => { if (mounted) setError(err.message) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  const mrr = stats ? (stats.users_active * MONTHLY_PRICE_EUR).toFixed(2) : null

  return (
    <div className="min-h-screen" style={{ background: "#060a13" }}>
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-5 pb-4 sticky top-0 z-20"
        style={{
          background: "rgba(6,10,19,0.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(30,41,59,0.4)",
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/users")}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-800/60 transition-colors cursor-pointer"
            style={{ background: "rgba(30,41,59,0.4)" }}
            aria-label="Retour à la liste"
          >
            <ArrowLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex-1">
            <h1 className="text-[18px] font-bold text-white tracking-tight flex items-center gap-2">
              <span style={{ color: "#06b6d4" }}>
                <BarChart3 className="w-4 h-4" />
              </span>
              Stats globales
            </h1>
            <p className="text-[11px] text-gray-500 mt-0.5">Mis à jour à chaque chargement</p>
          </div>
        </div>
      </motion.div>

      {/* CONTENU */}
      <div className="px-4 pt-5 pb-12">
        {error && (
          <div
            className="rounded-xl p-4 mb-4 text-[13px]"
            style={{
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.3)",
              color: "#fca5a5",
            }}
          >
            Erreur de chargement : {error}
          </div>
        )}

        {loading && !error && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          </div>
        )}

        {!loading && !error && stats && (
          <>
            {/* Section : Utilisateurs */}
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Utilisateurs
            </p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <StatCard
                label="Total inscrits"
                value={stats.users_total}
                color="#e5e7eb"
                delay={0}
              />
              <StatCard
                label="Essais en cours"
                value={stats.users_trial}
                sub="semaine non écoulée"
                color="#06b6d4"
                delay={0.04}
              />
              <StatCard
                label="Abonnés actifs"
                value={stats.users_active}
                color="#22c55e"
                delay={0.08}
              />
              <StatCard
                label="Essais terminés"
                value={stats.trials_expired}
                sub="sans conversion"
                color={stats.trials_expired > 0 ? "#f97316" : "#94a3b8"}
                delay={0.12}
              />
              <StatCard
                label="Churn 30j"
                value={stats.churn_30d}
                sub="résiliations"
                color={stats.churn_30d > 0 ? "#f97316" : "#22c55e"}
                delay={0.16}
              />
            </div>

            {/* Section : Revenus */}
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Revenus estimés
            </p>
            <div className="grid grid-cols-1 gap-3 mb-6">
              <StatCard
                label="MRR estimé"
                value={`${mrr} €`}
                sub={`${stats.users_active} actifs × ${MONTHLY_PRICE_EUR} €/mois`}
                color="#8b5cf6"
                delay={0.16}
              />
            </div>

            {/* Section : Engagement */}
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Activation
            </p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <StatCard
                label="Taux activation J+7"
                value={`${stats.activation_rate_7d} %`}
                sub={`${stats.activation_users_7d} / ${stats.activation_total} users`}
                color={stats.activation_rate_7d >= 50 ? "#22c55e" : stats.activation_rate_7d >= 25 ? "#f97316" : "#ef4444"}
                delay={0.2}
              />
              <div
                className="rounded-2xl p-4"
                style={{
                  background: "rgba(12,20,35,0.6)",
                  border: "1px solid rgba(30,41,59,0.55)",
                }}
              >
                <p className="text-[11px] text-gray-500 mb-1 uppercase tracking-wide">Définition</p>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  ≥ 1 séance complétée dans les 7 premiers jours suivant l'inscription.
                </p>
              </div>
            </div>

            {/* Section : Push admin */}
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Push admin (7 derniers jours)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Push envoyés"
                value={stats.push_sent_7d}
                color="#06b6d4"
                delay={0.24}
              />
              <StatCard
                label="Taux d'erreur"
                value={`${stats.push_error_rate_7d} %`}
                color={stats.push_error_rate_7d === 0 ? "#22c55e" : stats.push_error_rate_7d < 20 ? "#f97316" : "#ef4444"}
                delay={0.28}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
